import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { answerTextboxIntakeFormQuestion } from '@api';
import {
  cn,
  ErrorMessage,
  Field,
  Label,
  Paragraph,
  Textarea,
} from '@quickmd-llc/design-system';
import { TextboxQuestion } from '@qmd-types/intakeForms';
import { SubmitButton } from './SubmitButton';
import { useIntakeForm } from '../../context/useIntakeForm';

type Props = {
  question: TextboxQuestion;
};

type FormValues = {
  text: string;
};

const INPUT_MAX_LENGTH = 500;

export function TextboxForm({ question }: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { handleNextStep, intakeForm, updateAnswer } = useIntakeForm();
  const { handleSubmit, formState, register, watch } = useForm<FormValues>({
    defaultValues: {
      text: question.text || undefined,
    },
  });
  const { errors } = formState;
  const hasError = Boolean(errors.text);
  const textValue = watch('text');

  const inputOptions = {
    required: {
      value: question.isRequired,
      message: 'This field is required',
    },
    maxLength: {
      value: INPUT_MAX_LENGTH,
      message: `Maximum length is ${INPUT_MAX_LENGTH} characters`,
    },
  };

  async function onSubmit(data: FormValues) {
    const { intakeFormQuestionId } = question;
    const { intakeFormId } = intakeForm;

    setIsLoading(true);

    try {
      await answerTextboxIntakeFormQuestion({
        intakeFormQuestionId,
        intakeFormId,
        data,
      });

      const updatedQuestion = {
        ...question,
        text: data.text,
      };

      updateAnswer(intakeFormQuestionId, updatedQuestion);
      handleNextStep();
    } catch {
      // @TODO: Handle error
      setIsLoading(false);
    }
  }

  return (
    <form className="flex flex-1 flex-col" onSubmit={handleSubmit(onSubmit)}>
      <Field error={hasError}>
        <Label>{question.question}</Label>

        {hasError && <ErrorMessage>{errors?.text?.message}</ErrorMessage>}

        <Textarea
          {...register('text', inputOptions)}
          autoFocus
          placeholder="Enter your answer"
        />
        <Paragraph
          className={cn('text-right font-semibold', {
            'text-text-error': hasError,
          })}
          size="xs"
        >
          {textValue?.length || 0}/{INPUT_MAX_LENGTH}
        </Paragraph>
      </Field>

      <SubmitButton isLoading={isLoading} />
    </form>
  );
}
